'use client'

import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/Button'
import { MusiCalPopup } from '@/components/MusiCalPopup'
import TrialUrgency from '@/components/TrialUrgency'
import { AvatarStars } from '@/components/AvatarStars'

const videos = [
  '/images/dream/dream-bg.mp4',
  '/images/dream/2.mp4',
  '/images/dream/3.mp4',
  '/images/dream/4.mp4',
  '/images/dream/5.mp4',
]

export function Dream() {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [nextIndex, setNextIndex] = useState<number | null>(null)
  const [isInView, setIsInView] = useState(false)
  const sectionRef = useRef<HTMLElement | null>(null)
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([])

  // Lazy load: only activate when section is near viewport
  useEffect(() => {
    const section = sectionRef.current
    if (!section) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true)
          observer.disconnect()
        }
      },
      { rootMargin: '1500px' } // Start loading early (around ScaleNewHeights section)
    )

    observer.observe(section)
    return () => observer.disconnect()
  }, [])

  // Preload all videos only when in view
  useEffect(() => {
    if (!isInView) return
    videoRefs.current.forEach((video) => {
      if (video) {
        video.load()
      }
    })
  }, [isInView])

  // Start playing current video when in view
  useEffect(() => {
    if (!isInView) return
    const currentVideo = videoRefs.current[currentIndex]
    if (currentVideo) {
      currentVideo.currentTime = 0
      currentVideo.play().catch(() => {})
    }
  }, [currentIndex, isInView])

  // Preload next video when current is near end
  useEffect(() => {
    if (!isInView) return
    const currentVideo = videoRefs.current[currentIndex]
    if (!currentVideo) return

    const handleTimeUpdate = () => {
      const timeRemaining = currentVideo.duration - currentVideo.currentTime
      if (timeRemaining < 2) {
        const nextIdx = (currentIndex + 1) % videos.length
        const nextVideo = videoRefs.current[nextIdx]
        if (nextVideo) {
          nextVideo.currentTime = 0
          nextVideo.load()
        }
      }
    }

    currentVideo.addEventListener('timeupdate', handleTimeUpdate)
    return () => currentVideo.removeEventListener('timeupdate', handleTimeUpdate)
  }, [currentIndex, isInView])

  // Preload and start next video for crossfade
  useEffect(() => {
    if (nextIndex !== null) {
      const nextVideo = videoRefs.current[nextIndex]
      if (nextVideo) {
        nextVideo.currentTime = 0
        nextVideo.play().catch(() => {})
      }

      const timer = setTimeout(() => {
        setCurrentIndex(nextIndex)
        setNextIndex(null)
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, [nextIndex])

  const handleVideoEnd = () => {
    const next = (currentIndex + 1) % videos.length
    setNextIndex(next)
  }

  return (
    <section
      ref={sectionRef}
      className="relative min-h-[600px] overflow-hidden shadow-md sm:h-[70vh] md:h-[85vh] md:min-h-[600px]"
    >
      {/* Video Backgrounds with Crossfade */}
      {videos.map((src, index) => {
        const isActive = index === currentIndex
        const isNext = index === nextIndex
        const isVisible = isActive || isNext

        return (
          <video
            key={src}
            ref={(el) => { videoRefs.current[index] = el }}
            muted
            playsInline
            preload="none"
            poster={index === 0 ? '/images/dream/poster.jpg' : undefined}
            onEnded={isActive ? handleVideoEnd : undefined}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ease-in-out ${
              isActive && !nextIndex ? 'opacity-100' : ''
            }${isActive && nextIndex ? 'opacity-0' : ''}${isNext ? 'opacity-100' : ''}${!isVisible ? 'opacity-0' : ''}`}
            style={{ zIndex: isNext ? 1 : isActive ? 2 : 0 }}
          >
            <source src={src} type="video/mp4" />
          </video>
        )
      })}

      {/* Dark Overlay for Text Readability */}
      <div className="absolute inset-0 z-10 bg-black/60" />

      {/* Content */}
      <div className="relative z-20 flex h-full flex-col items-center justify-center px-4 py-12 text-center sm:py-8">
        <p className="text-sm font-medium uppercase tracking-widest text-green-300/80">
          Don&apos;t give up on your dreams
        </p>
        <h2 className="mt-3 max-w-3xl text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl">
          Imagine yourself playing the song that gives you chills...
        </h2>
        <p className="mt-6 max-w-xl text-lg text-gray-200 sm:text-xl">
          That moment when your fingers find the notes, and the music flows through you.
          Sarah, 42, felt it in week 3. So did 7-year-old Max. Your moment is waiting.
        </p>
        <div className="mt-10 flex flex-col items-center">
          <TrialUrgency tone="dark" />
          <MusiCalPopup>
            <Button
              className="bg-green-600 px-8 py-3 text-lg font-semibold text-white shadow-lg shadow-green-500/40 hover:bg-green-700 hover:shadow-green-500/60"
              variant="outline"
            >
              Claim Your Free Trial
            </Button>
          </MusiCalPopup>
          <div className="mt-2 text-sm text-gray-200">
            Enrol Only if you love it 🎶
          </div>
          <div className="mt-4 text-white [&_div]:text-white">
            <AvatarStars />
          </div>
        </div>
      </div>
    </section>
  )
}
